import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { cn } from '@/lib/utils'

interface LogoProps {
    className?: string
    /** The surface the logo sits on. Dark surfaces get a cream plate. */
    tone?: 'light' | 'dark'
    withWordmark?: boolean
    href?: string
}

/**
 * The Bazio brand marks.
 *
 * Three supplied assets, used as supplied and never redrawn, recoloured or
 * reconstructed from type:
 *
 * - `primary-logo.png`   the full `bazio` wordmark with the red sphere. The
 *                        main header lockup.
 * - `logo-black.png`     the compact solid-black wordmark, for places that
 *                        need the mark without the sphere's colour.
 * - `secondary-mark.png` the square `b` mark, for compact, sidebar and mobile
 *                        placements where the full wordmark cannot breathe.
 *
 * ALL THREE ARE BLACK ARTWORK ON TRANSPARENCY. There is no white variant in
 * the supplied set, so a mark may never be placed straight onto a dark
 * surface: pass `tone='dark'` and the component sits it on a cream plate,
 * which is the brand's own light-version lockup rather than a recolour.
 *
 * The wordmark is LANDSCAPE (2172x724, a 3:1 ratio), so size it by HEIGHT and
 * let the width follow: `h-7 w-auto`, never `size-*`, which would squash it.
 * The secondary mark is square (1254x1254).
 */

const WORDMARK = '/images/brand/primary-logo.png'
const WORDMARK_BLACK = '/images/brand/logo-black.png'
const MARK = '/images/brand/secondary-mark.png'

/** The square `b` mark. Decorative by default; label the link that wraps it. */
export const LogoMark: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={MARK}
            alt=''
            width={1254}
            height={1254}
            aria-hidden='true'
            className={cn('h-8 w-8 shrink-0 select-none object-contain', className)}
        />
    )
}

/** The full `bazio` wordmark, sphere included. */
export const LogoWordmark: FC<{ className?: string; variant?: 'primary' | 'black' }> = ({
    className,
    variant = 'primary'
}): ReactNode => {
    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={variant === 'black' ? WORDMARK_BLACK : WORDMARK}
            alt=''
            width={2172}
            height={724}
            aria-hidden='true'
            className={cn('h-7 w-auto shrink-0 select-none object-contain', className)}
        />
    )
}

/**
 * The header/footer lockup: the wordmark inside a link home.
 *
 * On a dark surface the artwork rides a cream plate, because the supplied set
 * is black-on-transparent only.
 */
export const Logo: FC<LogoProps> = ({ className, tone = 'light', withWordmark = true, href = '/' }): ReactNode => {
    return (
        <Link
            href={href}
            className={cn(
                'inline-flex items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2',
                tone === 'dark' && 'bg-canvas px-3 py-2 ring-1 ring-black/5',
                className
            )}
            aria-label='Bazio home'
        >
            {withWordmark ? <LogoWordmark /> : <LogoMark />}
        </Link>
    )
}
